import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'underscore';
import { useFormContext } from 'react-hook-form';

import AppChannel from 'modules/channels/App';
import { reactFormValidators } from 'modules/validation';

import useI18n from 'hooks/I18n/useI18n.js';

import Input, { ERROR_MODE } from 'src/components/atoms/Input/Input.jsx';
import Label from 'src/components/atoms/Label/Label.jsx';
import Select from 'src/components/atoms/Select/Select.jsx';
import Button from 'src/components/atoms/Button/Button.jsx';
import Text, { SMALL } from 'src/components/atoms/Text/Text.jsx';
import GoogleAuthButton, { SIGNIN_WITH } from 'src/components/atoms/Button/GoogleAuthButton.jsx';
import { LoadingAnimation } from 'components/ui';

import Title from 'src/components/atoms/Title/Title.jsx';

import Checkbox from 'src/components/molecules/Checkbox/Checkbox.jsx';
import Alert, { ERROR_TYPE } from 'src/components/molecules/Alert/Alert.jsx';

import PasswordValidation from 'src/components/organisms/PasswordValidation/PasswordValidation.jsx';

const Register = ({ onSubmit, googleCallback, countries, cities, refreshCitySelect }) => {
    const { i18n, trans } = useI18n();
    const {
        watch,
        register,
        setValue,
        setError,
        handleSubmit,
        resetField,
        formState: { errors, isSubmitting },
    } = useFormContext();
    const [googleAuthPending, setGoogleAuthPending] = useState(false);
    const handleGoogleCallback = res => {
        const onCallbackFail = () => setGoogleAuthPending(false);
        const onCallbackSucceed = () => setGoogleAuthPending(false);
        setGoogleAuthPending(true);
        googleCallback(res, { onCallbackFail, onCallbackSucceed });
    };
    const { assertNotBlank, assertValidEmail, assertValidPassword, assertChecked } = useMemo(
        () => reactFormValidators(trans),
        [trans]
    );

    const firstNameProps = {
        mode: errors.firstName && ERROR_MODE,
        error: get(errors, ['firstName', 'message']),
        ...register('firstName', {
            validate: assertNotBlank('firstName'),
        }),
    };

    const lastNameProps = {
        mode: errors.lastName && ERROR_MODE,
        error: get(errors, ['lastName', 'message']),
        ...register('lastName', { validate: assertNotBlank('lastName') }),
    };

    const mailProps = {
        mode: errors.email && ERROR_MODE,
        error: get(errors, ['email', 'message']),
        ...register('email', {
            validate: assertValidEmail('email'),
            setValueAs: v => v.trim(),
        }),
    };

    const passwordProps = {
        mode: errors.plainPassword && ERROR_MODE,
        error: get(errors, ['plainPassword', 'message']),
        ...register('plainPassword', {
            validate: assertValidPassword('plainPassword'),
            setValueAs: v => v.trim(),
        }),
    };

    const acceptedTOSProps = {
        error: get(errors, ['hasAcceptedTOS', 'message']),
        ...register('hasAcceptedTOS', { validate: assertChecked('hasAcceptedTOS') }),
    };

    const cityProps = {
        ...register('city', {
            validate: assertNotBlank('city'),
            onChange: e => {
                setValue('city', e.target.value);
                setValue('cityId', e.target.value);
            },
        }),
    };

    const [email, password, country] = watch(['email', 'plainPassword', 'countries']);
    const alert = get(errors, ['root', 'alert'], {});
    const googleOAuthAlert = get(errors, ['root', 'googleOAuth', 'alert'], {});

    const zipCodeProps = {
        ...register('zipCode', {
            onChange: e => {
                refreshCitySelect(country, e.target.value);
            },
        }),
    };

    useEffect(() => {
        if (cities.length < 1) {
            return;
        }

        setValue('city', cities[0].value);
        setValue('cityId', cities[0].value);
    }, [cities, setValue]);

    useEffect(() => {
        const setServerError = err => {
            const error = get(err, ['responseJSON', 'error']);
            if (error === 'invalid_grant') {
                setError('root.alert', {
                    type: 'error',
                    message: 'session.loginForm.invalidPassword',
                });
            }

            if (
                error === 'invalid_request' &&
                get(err, ['responseJSON', 'hint']) === 'user_already_exist'
            ) {
                setError('root.googleOAuth.alert', {
                    type: 'error',
                    message: 'session.loginForm.userAlreadyExist',
                });
            } else {
                setError('root.alert', {
                    type: 'error',
                    message: 'global.error.retryMessage',
                });
            }
        };
        AppChannel.vent.on('auth:error', setServerError);

        return () => {
            AppChannel.vent.off('auth:error', setServerError);
        };
    }, [setError]);

    return (
        <div className="nd-register-container">
            <Title level="3">{trans('global.registration')}</Title>
            <Text color="secondary">{trans('anonymous.registration.message')}</Text>
            {googleCallback && process.env.GOOGLE_CLIENT_ID && (
                <>
                    {googleAuthPending ? (
                        <LoadingAnimation type="pi-spinner" />
                    ) : (
                        <>
                            <Alert
                                visible={!isEmpty(googleOAuthAlert)}
                                type={googleOAuthAlert.type || ERROR_TYPE}
                                message={
                                    googleOAuthAlert.message && trans(googleOAuthAlert.message)
                                }
                            />
                            <GoogleAuthButton
                                clientId={process.env.GOOGLE_CLIENT_ID}
                                text={SIGNIN_WITH}
                                callback={handleGoogleCallback}
                            />
                        </>
                    )}
                    <div className="nd-register-separator">
                        <Text size={SMALL}>{trans('global.or')}</Text>
                    </div>
                    <Alert
                        visible={!isEmpty(alert)}
                        type={alert.type || ERROR_TYPE}
                        message={alert.message && trans(alert.message)}
                    />
                </>
            )}
            <div className="nd-register-container-login">
                <Text>{trans('session.alreadyAccount.title')}</Text>
                <div
                    className="nd-link nd-text strong"
                    dangerouslySetInnerHTML={{
                        __html: trans('session.alreadyAccount.text', { '%url%': '/login' }),
                    }}
                />
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Input
                    {...firstNameProps}
                    id="firstName"
                    placeholder={trans('global.placeHolder.firstName')}
                />
                <Input
                    {...lastNameProps}
                    id="lastName"
                    placeholder={trans('global.placeHolder.lastName')}
                />
                <Input {...mailProps} id="email" placeholder={trans('global.placeHolder.email')} />
                <PasswordValidation password={passwordProps} id="plainPassword" />
                <div>
                    <Label htmlFor="countries">{trans('address.country')}</Label>
                    <Select
                        {...register('countries', {
                            onChange: e => {
                                resetField('zipCode');
                                resetField('city');
                                resetField('cityId');
                                refreshCitySelect(e.target.value, '');
                            },
                        })}
                        placeholder={trans('address.countryChoose')}
                        id="countries"
                        options={countries}
                    />
                </div>
                <div>
                    <Label htmlFor="zipCode">{trans('address.zip')}</Label>
                    <Input {...zipCodeProps} id="zipCode" />
                </div>
                <div>
                    <Label htmlFor="city">{trans('address.city')}</Label>
                    <Select
                        {...cityProps}
                        id="city"
                        options={cities}
                        readOnly={cities.length < 2}
                    />
                </div>
                <Checkbox {...acceptedTOSProps} id="hasAcceptedTOS">
                    <div
                        className="nd-text nd-link"
                        dangerouslySetInnerHTML={{
                            __html: trans('user.tos', { '%url%': `/${i18n}/p/terms` }),
                        }}
                    ></div>
                </Checkbox>
                <Button disabled={!(email && password) || isSubmitting} size="big" type="submit">
                    {trans('global.submit.signUp')}
                </Button>
            </form>
        </div>
    );
};

Register.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    googleCallback: PropTypes.func.isRequired,
    countries: PropTypes.array,
    cities: PropTypes.array,
    refreshCitySelect: PropTypes.func.isRequired,
};
Register.defaultProps = {
    countries: [],
    cities: [],
    googleCallback: () => null,
};

export default Register;
